import { useEffect, useMemo, useState } from "react";
import {
  FolderKanban, FolderOpen, FileText, Image as ImageIcon, Table2, Download, Search, Loader2, Paperclip, Images, Folder, ChevronLeft,
} from "lucide-react";
import { useProjectStore } from "../store/useProjectStore";
import { useAuthStore } from "../store/useAuthStore";
import { API_BASE_URL } from "../lib/api";
import { axiosInstance } from "../lib/axios";

const fmtSize = (b?: number) => {
  if (!b) return "";
  const k = 1024, sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${parseFloat((b / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};
const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const fileIcon = (name = "", cls: string) => {
  if (/\.(jpg|jpeg|png|gif|webp)$/i.test(name)) return <ImageIcon className={cls} />;
  if (/\.(xls|xlsx|csv)$/i.test(name)) return <Table2 className={cls} />;
  return <FileText className={cls} />;
};

// Business-Developer Files page — every file across the BD's projects,
// organized per project. Files come from each project's attachments.
export default function BDFilesPage() {
  const { projects, fetchProjects } = useProjectStore();
  const { authUser } = useAuthStore();
  // Admins render under the fixed global Navbar (h-16); offset to clear it.
  const isBD = authUser?.role === "Business Developer";
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [q, setQ] = useState("");
  // Portfolio samples uploaded through the Proposal Builder live here too —
  // they're the BD's reusable File Storage assets.
  const [portfolio, setPortfolio] = useState<any[]>([]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get("/proposals/portfolio-assets");
        setPortfolio(data.assets || []);
      } catch { setPortfolio([]); }
    })();
  }, []);
  useEffect(() => {
    if (!selectedId && projects.length) setSelectedId(projects[0]._id);
  }, [projects, selectedId]);

  const withFiles = useMemo(
    () => projects.map((p: any) => ({ ...p, _files: (p.attachments || []) })),
    [projects]
  );
  const selected: any = withFiles.find((p) => p._id === selectedId) || null;
  const files: any[] = useMemo(() => {
    const list = selected?._files || [];
    if (!q.trim()) return list;
    return list.filter((f: any) => (f.name || "").toLowerCase().includes(q.toLowerCase()));
  }, [selected, q]);

  const download = (att: any) => window.open(`${API_BASE_URL}/project/${selectedId}/attachments/${att._id}/download`, "_blank");

  // Group files by category for organization.
  const groups = useMemo(() => {
    const g: Record<string, any[]> = {};
    files.forEach((f) => { const c = f.category || "Other"; (g[c] = g[c] || []).push(f); });
    return g;
  }, [files]);

  // Left rail selects either a PROJECT or a LIBRARY category (Portfolio, etc.).
  const [libView, setLibView] = useState<null | "portfolio">(null);
  // Folder view for a project — the right panel shows folders (per category),
  // and opening one reveals its files.
  const [openFolder, setOpenFolder] = useState<string | null>(null);
  useEffect(() => { setOpenFolder(null); }, [selectedId, libView]);
  const portfolioFiltered = useMemo(
    () => portfolio.filter((a) => !q.trim() || (a.name || "").toLowerCase().includes(q.toLowerCase())),
    [portfolio, q]
  );
  const folders = useMemo(() => {
    return Object.entries(groups).map(([cat, arr]) => ({ key: cat, label: cat, files: arr as any[], kind: "attachment" as const }));
  }, [groups]);
  const activeFolder = folders.find((f) => f.key === openFolder) || null;
  const openProject = (id: string) => { setSelectedId(id); setLibView(null); };

  return (
    <div className={`flex h-[calc(100vh-64px)] bg-white overflow-hidden ${isBD ? "" : "mt-16"}`}>
      {/* Projects sidebar */}
      <aside className="w-64 shrink-0 border-r border-outline-variant bg-surface-container-low flex flex-col">
        <div className="px-4 py-4 border-b border-outline-variant">
          <h2 className="text-sm font-bold text-on-surface flex items-center gap-2"><FolderKanban size={16} className="text-primary" /> Your Projects</h2>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1 cre-scroll">
          {withFiles.length === 0 && <p className="text-[12px] text-outline px-2 py-2">No projects yet.</p>}
          {withFiles.map((p) => {
            const count = p._files.length;
            const active = !libView && p._id === selectedId;
            return (
              <button key={p._id} onClick={() => openProject(p._id)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left transition-colors ${active ? "bg-primary text-white" : "text-on-surface-variant hover:bg-surface-container"}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <FolderOpen size={15} className="shrink-0" />
                  <span className="min-w-0">
                    <span className="text-[13px] truncate block">{p.name}</span>
                    {!isBD && (p as any).createdBy?.fullName && (
                      <span className={`text-[10px] truncate block ${active ? "text-white/70" : "text-outline"}`}>BD: {(p as any).createdBy.fullName}</span>
                    )}
                  </span>
                </span>
                {count > 0 && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-surface-container text-on-surface-variant"}`}>{count}</span>}
              </button>
            );
          })}

          {/* Library — BD-wide reusable files (not tied to a project) */}
          <div className="pt-3 mt-2 border-t border-outline-variant">
            <p className="text-[10px] font-bold text-outline uppercase tracking-[0.12em] px-3 pb-1">Library</p>
            <button onClick={() => setLibView("portfolio")}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left transition-colors ${libView === "portfolio" ? "bg-primary text-white" : "text-on-surface-variant hover:bg-surface-container"}`}>
              <span className="flex items-center gap-2 min-w-0"><Images size={15} className="shrink-0" /><span className="text-[13px] truncate">Portfolio Samples</span></span>
              {portfolio.length > 0 && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${libView === "portfolio" ? "bg-white/20" : "bg-surface-container text-on-surface-variant"}`}>{portfolio.length}</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Files panel */}
      <section className="flex-1 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant">
          <div>
            <h1 className="text-xl font-bold text-on-surface">{libView === "portfolio" ? "Portfolio Samples" : (selected?.name || "Files")}</h1>
            <p className="text-[12px] text-on-surface-variant">
              {libView === "portfolio"
                ? `${portfolioFiltered.length} reusable file${portfolioFiltered.length === 1 ? "" : "s"}`
                : `${selected?.assignedContact?.name || ""}${selected ? ` · ${files.length} file${files.length === 1 ? "" : "s"}` : ""}`}
            </p>
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-outline" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files…" className="pl-9 pr-3 py-2 rounded-lg bg-background border border-outline-variant text-sm outline-none focus:border-primary w-56" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 cre-scroll">
          {libView === "portfolio" ? (
            /* ── Library: Portfolio Samples ── */
            portfolioFiltered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-outline">
                <Images size={40} className="opacity-20 mb-3" />
                <p className="text-sm font-semibold">No portfolio samples yet.</p>
                <p className="text-[12px]">Images added in the Proposal Builder appear here.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {portfolioFiltered.map((a: any) => (
                  <div key={a._id} className="group border border-outline-variant rounded-xl overflow-hidden hover:shadow-md transition-all bg-white">
                    <img src={a.url} alt={a.name} className="h-24 w-full object-cover" />
                    <div className="p-3">
                      <div className="font-semibold text-[13px] text-on-surface truncate" title={a.name}>{a.name}</div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[11px] text-outline">{fmtSize(a.size)} · {fmtDate(a.createdAt)}</span>
                        <a href={a.url} target="_blank" rel="noreferrer" title="Open" className="text-outline hover:text-primary"><Download size={15} /></a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : !selected ? (
            <div className="grid place-items-center h-full text-outline"><Loader2 className="animate-spin text-primary" size={22} /></div>
          ) : activeFolder ? (
            /* ── Inside a folder ── */
            <>
              <button onClick={() => setOpenFolder(null)} className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-primary font-semibold hover:underline">
                <ChevronLeft size={16} /> Folders
              </button>
              <h3 className="text-[15px] font-bold text-on-surface mb-3 flex items-center gap-2">
                <FolderOpen size={18} className="text-primary" /> {activeFolder.label} <span className="text-outline font-normal">({activeFolder.files.length})</span>
              </h3>
              {activeFolder.kind === "portfolio" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {activeFolder.files.map((a: any) => (
                    <div key={a._id} className="group border border-outline-variant rounded-xl overflow-hidden hover:shadow-md transition-all bg-white">
                      <img src={a.url} alt={a.name} className="h-24 w-full object-cover" />
                      <div className="p-3">
                        <div className="font-semibold text-[13px] text-on-surface truncate" title={a.name}>{a.name}</div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[11px] text-outline">{fmtSize(a.size)} · {fmtDate(a.createdAt)}</span>
                          <a href={a.url} target="_blank" rel="noreferrer" title="Open" className="text-outline hover:text-primary"><Download size={15} /></a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {activeFolder.files.map((f: any) => (
                    <div key={f._id} className="group border border-outline-variant rounded-xl p-4 hover:shadow-md transition-all bg-white">
                      <div className="h-20 bg-surface-container-low rounded-lg grid place-items-center mb-3">{fileIcon(f.name, "w-9 h-9 text-primary")}</div>
                      <div className="font-semibold text-[13px] text-on-surface truncate" title={f.name}>{f.name}</div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[11px] text-outline">{fmtSize(f.size)} · {fmtDate(f.uploadedAt)}</span>
                        <button onClick={() => download(f)} title="Download" className="text-outline hover:text-primary"><Download size={15} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : folders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-outline">
              <Paperclip size={40} className="opacity-20 mb-3" />
              <p className="text-sm font-semibold">No files yet.</p>
              <p className="text-[12px]">Upload attachments from the project — they'll appear as folders here.</p>
            </div>
          ) : (
            /* ── Folder grid ── */
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {folders.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setOpenFolder(f.key)}
                  className="flex flex-col items-center gap-2 p-5 rounded-2xl border border-outline-variant bg-white hover:shadow-md hover:border-primary/40 transition-all text-center"
                >
                  {f.kind === "portfolio" ? <Images size={40} className="text-amber-500" /> : <Folder size={40} className="text-primary" fill="currentColor" fillOpacity={0.12} />}
                  <span className="text-[13px] font-semibold text-on-surface truncate max-w-full" title={f.label}>{f.label}</span>
                  <span className="text-[11px] text-outline">{f.files.length} file{f.files.length === 1 ? "" : "s"}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
